import { FC, ReactElement, useMemo } from 'react';

import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { Picture, ProductPrice } from '../../components';
import { IconButton, ProductLabels, WishlistButton } from '../../compositions';
import { Product } from '../../entities/@products/Product/Product';
import { compareCartQuantityWithStock } from '../../entities/@products/Product/ProductHelpers';
import { defaultImage } from '../../entities/Media/MediaTransformers';
import { AppRoute, appRoutes } from '../../entities/Routing/Routing';
import { trimToMaxLength } from '../../helpers/string';
import { replaceUrlParamKeysWithValues } from '../../helpers/url';
import { useDeviceWidth, useTrans } from '../../hooks';

import './ProductCard.scss';

interface ProductCardProps {
    cartIsLoading: boolean;
    wishlistIsLoading: boolean;
    isGridView?: boolean;
    isInWishlist: boolean;
    product: Product;
    inCartQuantity: number;
    onAddToCart: () => void;
    onClickWishlistButton: () => void;
    className?: string;
    pictureWrapperClassName?: string;
    pictureClassName?: string;
    imageClassName?: string;
    secondaryImageClassName?: string;
}

export const ProductCard: FC<ProductCardProps> = ({
    cartIsLoading,
    wishlistIsLoading,
    isGridView,
    isInWishlist,
    product,
    inCartQuantity,
    onAddToCart,
    onClickWishlistButton,
    className = '',
    pictureWrapperClassName = '',
    pictureClassName = '',
    imageClassName = '',
    secondaryImageClassName = '',
}): ReactElement => {
    const trans = useTrans();
    const { isMobile } = useDeviceWidth();

    const productImages = product.mediaItems.filter(mediaItem => mediaItem.image);

    const truncatedName = useMemo((): string => {
        const maxTitleLength = isMobile ? 32 : 40;

        return trimToMaxLength(product.name, maxTitleLength);
    }, [isMobile, product.name]);

    const productPath = trans(appRoutes[AppRoute.productDetail].path);
    const productUrl = replaceUrlParamKeysWithValues(productPath, {
        slug: product.relativeUrl,
    });

    const brandDetailPath = trans(appRoutes[AppRoute.brandDetail].path);
    const brandDetailUrl = replaceUrlParamKeysWithValues(brandDetailPath, {
        slug: product.brand?.href,
    });

    const inStock = compareCartQuantityWithStock(product.stockQuantity, inCartQuantity);

    const pictureWrapperClassNames = classNames('product-card__picture-wrapper', {
        'product-card__picture-wrapper--is-grid-view': isGridView,
        'product-card__picture-wrapper--has-secondary-image': productImages.length > 1,
    }, pictureWrapperClassName);

    const pictureClassNames = classNames('product-card__image-default', {
        'product-card__image-default--is-fallback': productImages.length === 0,
        'product-card__image-default--is-sold-out': !inStock,
        'product-card__image-switch-primary': productImages.length > 1,
        'product-card__image-switch-primary--is-sold-out': !inStock,
    }, pictureClassName);

    const secondaryPictureClassNames = classNames('product-card__picture-switch-secondary', {
        'product-card__picture-switch-secondary--is-sold-out': !inStock,
    }, pictureClassName);

    return (
        <div className={`product-card ${className}`}>
            <div className={pictureWrapperClassNames}>
                <ProductLabels
                    isInStock={inStock}
                    isNew={product.isNew}
                    price={product.price}
                    marketingAction={product.marketingAction}
                    className="product-card__labels"
                />

                <Link to={productUrl} className="product-card__picture-inner-wrapper">
                    {productImages.length === 0 && (
                        <Picture
                            {...defaultImage()}
                            loading="lazy"
                            className={pictureClassNames}
                            imageClassName={imageClassName}
                        />
                    )}

                    {productImages.length > 0 && (
                        <Picture
                            {...productImages[0].image}
                            loading="lazy"
                            className={pictureClassNames}
                            imageClassName={imageClassName}
                        />
                    )}

                    {productImages.length > 1 && (
                        <Picture
                            {...productImages[1].image}
                            loading="lazy"
                            className={secondaryPictureClassNames}
                            imageClassName={secondaryImageClassName}
                        />
                    )}
                </Link>

                <div className="product-card__buttons">
                    <IconButton
                        isSmall
                        icon="cart"
                        disabled={!inStock || cartIsLoading}
                        text={trans(`containers.productCard.${inStock ? 'add' : 'soldOut'}`)}
                        onClick={onAddToCart}
                        className="product-card__cart-button"
                    />

                    <WishlistButton
                        isSmall
                        isActive={isInWishlist}
                        disabled={wishlistIsLoading}
                        text={isInWishlist ? trans('common.removeFromWishlist') : trans('common.addToWishlist')}
                        onClick={onClickWishlistButton}
                        className="product-card__wishlist-button"
                    />
                </div>
            </div>

            <div className="product-card__info">
                {product.brand && (
                    <Link to={brandDetailUrl} className="product-card__brand-link">
                        {product.brand.label}
                    </Link>
                )}

                <Link
                    title={product.name}
                    to={productUrl}
                    className="product-card__product-link"
                >
                    {truncatedName}
                </Link>

                <ProductPrice
                    {...product.price}
                    className="product-card__price"
                />

                {product.variantsCount && product.variantsCount > 1 && (
                    <p className="product-card__variants">
                        <span>
                            {trans('containers.productCard.availableIn')}
                        </span>

                        <span className="product-card__variants-amount">
                            {trans('containers.productCard.variants', { count: product.variantsCount })}
                        </span>
                    </p>
                )}
            </div>
        </div>
    );
};
